import React, { memo, useState } from "react";
import {
  MapPin,
  Clock,
  Phone,
  Mail,
  Globe,
  Shield,
  Building,
  Info,
  AlertTriangle
} from "lucide-react";

const AboutHotel = memo(({ hotelData, hotelContent, hotelDescriptive }) => {
  const [activeTab, setActiveTab] = useState('location');

  // Simplified data extraction
  const extractHotelData = () => {
    const hotelInfo = hotelData?.hotelInfo || hotelData?.HotelInfo || {};
    const descriptiveInfo = hotelData?.descriptive?.GetHotelDescriptiveInfoRS?.HotelDescriptiveInfos?.HotelDescriptiveInfo?.[0] ||
                           hotelData?.HotelDescriptiveInfo || hotelDescriptive || {};
    
    return {
      locationInfo: descriptiveInfo?.LocationInfo || {},
      propertyInfo: descriptiveInfo?.PropertyInfo || {},
      securityFeatures: descriptiveInfo?.SecurityFeatures?.SecurityFeature || [],
      policies: descriptiveInfo?.Policies?.Policy || [],
      descriptions: descriptiveInfo?.Descriptions?.Description?.reduce((acc, desc) => {
        if (desc.Text) {
          const type = desc.Text.Type;
          const value = desc.Text.value;
          if (type && value) acc[type] = value;
        }
        return acc;
      }, {}) || hotelData?.hotelInfo?.descriptions || {},
      contact: hotelInfo.contact || descriptiveInfo?.LocationInfo?.Contact || {},
      address: hotelInfo.address || descriptiveInfo?.LocationInfo?.Address || {}
    };
  };

  const {
    locationInfo,
    propertyInfo,
    securityFeatures,
    policies,
    descriptions,
    contact,
    address
  } = extractHotelData();

  // Contact details
  const phone = contact.phone || contact.Phone || '';
  const email = contact.email || contact.Email || '';
  const website = contact.website || contact.Website || '';

  // Address details
  const addressLines = address.addressLines || address.AddressLine || [];
  const city = address.city?.value || address.city || address.CityName?.value || address.CityName || '';
  const state = address.state?.value || address.state || address.StateProv?.StateCode || '';
  const country = address.country?.value || address.country || address.CountryName?.value || address.CountryName || '';
  const postalCode = address.postalCode || address.PostalCode || '';

  // Property details
  const checkInTime = propertyInfo.CheckInTime || '15:00';
  const checkOutTime = propertyInfo.CheckOutTime || '11:00';
  const totalRooms = propertyInfo.TotalRooms || 0;
  const floors = propertyInfo.Floors || 0;
  const yearBuilt = propertyInfo.YearBuilt || '';
  const lastRenovated = propertyInfo.LastRenovated || '';


  const tabs = [
    { id: 'location', label: 'Location & Contact', icon: MapPin },
    { id: 'policies', label: 'Policies', icon: Shield },
    { id: 'safety', label: 'Safety & Security', icon: AlertTriangle }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'location':
        return (
          <div className="space-y-6">
            {/* Hotel Description */}
            {descriptions.General && (
              <div className="text-center mb-6">
                <h3 className="text-xl font-light mb-3 text-slate-800 typography-subsection-title">
                  About This Hotel
                </h3>
                <p className="text-slate-600 leading-relaxed font-light max-w-4xl mx-auto">{descriptions.General}</p>
              </div>
            )}

            {/* Property Highlights */}
            <div className="text-center mb-6">
              <h3 className="text-xl font-light mb-4 text-slate-800 typography-subsection-title">
                Property Information
              </h3>
              <div className="flex flex-wrap justify-center gap-4">
                {floors > 0 && (
                  <div className="flex flex-col items-center gap-3 p-4 bg-slate-50 rounded-xl min-w-[140px] hover:bg-slate-100 transition-colors duration-200">
                    <div className="w-10 h-10 bg-slate-200 rounded-xl flex items-center justify-center">
                      <Building className="w-5 h-5 text-slate-600" />
                    </div>
                    <div className="text-center">
                      <p className="text-xs font-light text-slate-500 uppercase tracking-wide">Floors</p>
                      <p className="text-lg font-light text-slate-800">{floors}</p>
                    </div>
                  </div>
                )}
                
                {yearBuilt && (
                  <div className="flex flex-col items-center gap-3 p-4 bg-slate-50 rounded-xl min-w-[140px] hover:bg-slate-100 transition-colors duration-200">
                    <div className="w-10 h-10 bg-slate-200 rounded-xl flex items-center justify-center">
                      <Building className="w-5 h-5 text-slate-600" />
                    </div>
                    <div className="text-center">
                      <p className="text-xs font-light text-slate-500 uppercase tracking-wide">Year Built</p>
                      <p className="text-lg font-light text-slate-800">{yearBuilt}</p>
                    </div>
                  </div>
                )}
                
                {lastRenovated && (
                  <div className="flex flex-col items-center gap-3 p-4 bg-slate-50 rounded-xl min-w-[140px] hover:bg-slate-100 transition-colors duration-200">
                    <div className="w-10 h-10 bg-slate-200 rounded-xl flex items-center justify-center">
                      <Building className="w-5 h-5 text-slate-600" />
                    </div>
                    <div className="text-center">
                      <p className="text-xs font-light text-slate-500 uppercase tracking-wide">Last Renovated</p>
                      <p className="text-lg font-light text-slate-800">{lastRenovated}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Check-in/Check-out Times */}
            <div className="text-center mb-6">
              <h3 className="text-xl font-light mb-4 text-slate-800 typography-subsection-title">
                Check-in & Check-out
              </h3>
              <div className="grid grid-cols-2 gap-4 max-w-md mx-auto">
                <div className="flex flex-col items-center gap-3 p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors duration-200">
                  <div className="w-10 h-10 bg-slate-200 rounded-xl flex items-center justify-center">
                    <Clock className="w-5 h-5 text-slate-600" />
                  </div>
                  <div className="text-center">
                    <p className="text-xs font-light text-slate-500 uppercase tracking-wide">Check-in</p>
                    <p className="text-lg font-light text-slate-800">{checkInTime}</p>
                  </div>
                </div>
                
                <div className="flex flex-col items-center gap-3 p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors duration-200">
                  <div className="w-10 h-10 bg-slate-200 rounded-xl flex items-center justify-center">
                    <Clock className="w-5 h-5 text-slate-600" />
                  </div>
                  <div className="text-center">
                    <p className="text-xs font-light text-slate-500 uppercase tracking-wide">Check-out</p>
                    <p className="text-lg font-light text-slate-800">{checkOutTime}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Address & Contact */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Address */}
              <div className="text-center">
                <h3 className="text-xl font-light mb-4 text-slate-800 typography-subsection-title">
                  Address
                </h3>
                <div className="bg-slate-50 rounded-xl p-5">
                  {addressLines.length > 0 && (
                    <p className="mb-3 text-sm font-light text-slate-600">{addressLines.join(', ')}</p>
                  )}
                  <p className="text-slate-700 text-sm font-light">
                    {[city, state, postalCode, country].filter(Boolean).join(', ')}
                  </p>
                </div>
              </div>

              {/* Contact Information */}
              <div className="text-center">
                <h3 className="text-xl font-light mb-4 text-slate-800 typography-subsection-title">
                  Contact Information
                </h3>
                <div className="bg-slate-50 rounded-xl p-5 space-y-3">
                  {phone && (
                    <div className="flex items-center justify-center gap-3">
                      <Phone className="w-4 h-4 text-slate-600" />
                      <span className="text-sm font-light text-slate-700">{phone}</span>
                    </div>
                  )}
                  
                  {email && (
                    <div className="flex items-center justify-center gap-3">
                      <Mail className="w-4 h-4 text-slate-600" />
                      <span className="text-sm font-light text-slate-700">{email}</span>
                    </div>
                  )}
                  
                  {website && (
                    <div className="flex items-center justify-center gap-3">
                      <Globe className="w-4 h-4 text-slate-600" />
                      <a 
                        href={website.startsWith('http') ? website : `https://${website}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-slate-600 hover:text-slate-800 underline text-sm font-light transition-colors duration-200 break-all text-left"
                      >
                        {website}
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Location Highlights */}
            {descriptions.Location && (
              <div className="text-center mt-6">
                <h3 className="text-xl font-light mb-4 text-slate-800 typography-subsection-title">
                  Location Highlights
                </h3>
                <div className="bg-slate-50 rounded-xl p-5">
                  <p className="text-slate-600 leading-relaxed font-light">{descriptions.Location}</p>
                </div>
              </div>
            )}
          </div>
        );


      case 'policies':
        return (
          <div className="space-y-6">
            {policies.length > 0 ? (
              policies.map((policy, index) => (
                <div key={index} className="text-center">
                  <div className="flex items-center justify-center gap-3 mb-4">
                    <div className="w-8 h-8 bg-slate-200 rounded-xl flex items-center justify-center">
                      <Shield className="w-4 h-4 text-slate-600" />
                    </div>
                    <h3 className="text-xl font-light text-slate-800 typography-subsection-title">
                      {policy.Type || 'Hotel Policy'}
                    </h3>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-5 max-w-4xl mx-auto">
                    <p className="text-slate-700 leading-relaxed text-sm font-light">
                      {policy.Description || policy}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8">
                <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Info className="w-6 h-6 text-slate-400" />
                </div>
                <h3 className="text-xl font-light text-slate-800 mb-2 typography-subsection-title">
                  No Policies Available
                </h3>
                <p className="text-slate-500 text-sm font-light">No specific policies information available at this time.</p>
              </div>
            )}
          </div>
        );

      case 'safety':
        return (
          <div className="space-y-6">
            {securityFeatures.length > 0 && (
              <div className="text-center">
                <div className="flex items-center justify-center gap-3 mb-4">
                  <div className="w-8 h-8 bg-slate-200 rounded-xl flex items-center justify-center">
                    <Shield className="w-4 h-4 text-slate-600" />
                  </div>
                  <h3 className="text-xl font-light text-slate-800 typography-subsection-title">
                    Security & Safety Features
                  </h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {securityFeatures.map((feature, index) => (
                    <div key={index} className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors duration-200">
                      <Shield className="w-4 h-4 text-slate-600 flex-shrink-0" />
                      <span className="text-sm text-slate-700 font-light">{feature.Description || feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* General Safety Information */}
            <div className="text-center">
              <div className="flex items-center justify-center gap-3 mb-4">
                <div className="w-8 h-8 bg-slate-200 rounded-xl flex items-center justify-center">
                  <AlertTriangle className="w-4 h-4 text-slate-600" />
                </div>
                <h3 className="text-xl font-light text-slate-800 typography-subsection-title">
                  Important Safety Information
                </h3>
              </div>
              <div className="space-y-3">
                {[
                  'Please follow all hotel safety guidelines and emergency procedures',
                  'Keep your room key secure and report any suspicious activity',
                  'Familiarize yourself with emergency exits and evacuation routes',
                  'Contact hotel staff immediately for any safety concerns'
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-3 p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors duration-200">
                    <div className="w-4 h-4 bg-slate-200 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <div className="w-1.5 h-1.5 bg-slate-600 rounded-full"></div>
                    </div>
                    <span className="text-sm text-slate-700 font-light">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {securityFeatures.length === 0 && (
              <div className="text-center py-8">
                <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-6 h-6 text-slate-400" />
                </div>
                <h3 className="text-xl font-light text-slate-800 mb-2 typography-subsection-title">
                  Safety Information
                </h3>
                <p className="text-slate-500 text-sm font-light">General safety guidelines are provided above.</p>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      {/* Tab Navigation */}
      <div className="border-b border-gray-200 bg-gradient-to-r from-slate-50 to-white">
        <div className="flex justify-center overflow-x-auto scrollbar-hide">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`group flex items-center justify-center gap-2 sm:gap-3 px-4 sm:px-8 py-3 sm:py-4 text-xs sm:text-sm font-light whitespace-nowrap transition-all duration-300 border-b-2 ${
                  activeTab === tab.id
                    ? 'border-slate-800 text-slate-800 bg-white shadow-sm'
                    : 'border-transparent text-slate-600 hover:text-slate-800 hover:bg-white/50'
                }`}
              >
                <Icon className={`w-4 h-4 transition-colors duration-300 ${
                  activeTab === tab.id ? 'text-slate-800' : 'text-slate-500 group-hover:text-slate-700'
                }`} />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Content */}
      <div className="p-6">
        {renderTabContent()}
      </div>
    </div>
  );
});

AboutHotel.displayName = 'AboutHotel';

export default AboutHotel;
